// CASL <https://github.com/stalniy/casl> based authorization setup for K4Ops.

import { get, groupBy, isFunction, map } from "lodash";
import { Ability, AbilityBuilder, ForbiddenError } from "@casl/ability";

// Expose underlying ForbiddenError exception type. Downstream users of this
// service don't need to know where it comes from, just what to check for.
export { ForbiddenError } from "@casl/ability";

// The shared "ability" of the current user. This should be used throughout the
// rest of the app for `can` and `cannot` checks. Starts as an anonymous user
// and needs to be replaced/updated via `defineAbilityFor` once a user is
// authenticated.
const ability = defineAbilityFor(null);
export default ability;

const rolesMap = Object.freeze({
	Admin(user, { can, cannot }) {
		// Users with admin role get blanket permission to do everything.
		can("manage", "all");
		// lock access is not part of admin - must be expressly granted
		cannot("manage", "LockManager");
	},

	LockUser(user, { can }) {
		can("read", "LockManager");
	},

	LockAdmin(user, { can }) {
		can("manage", "LockManager");
	},

	Support(user, { can }) {
		can("read", "LiveView");
		can("read", "K4NOC");
	},

	"Advanced Support"(user, { can }) {
		can("manage", "LiveView");
		can("manage", "K4NOC");
		can("manage", "Scenes");
		can("manage", "Users");
	},

	"Check-in"(user, { can }) {
		can("Check-in", "Users");
	},

	"Project Manager"(user, { can }) {
		can("read", "Report"); // Can view (all) DOMO reports
	},

	"Advanced Project Manager"(user, { can }) {
		can("manage", "Report"); // Can view (all) DOMO reports
		can("manage", "AccessGroups");
	},
});
export const supportedRoles = Object.freeze(Object.keys(rolesMap));

const permissionsMap = Object.freeze({
	OpsBasicUser(user, { can }) {
		// With valid credentials, user is, at the very least, allowed to access
		// Ops related applications.
		can("access", "Ops");
	},

	OpsDevicesRead(user, { can }) {
		can("list", "Device");
		can("read", "Device");
		can("command", "Device"); // Can execute commands on a device
		can("test", "Device"); // Can run device tests
	},

	OpsDevicesWrite(user, { can }) {
		can("install", "System"); // Can navigate installation flow
		can("create", "Device");
		can("delete", "Device");
		can("update", "Device"); // Can move a device and change properties
	},
});
export const supportedPermissions = Object.freeze(Object.keys(permissionsMap));

const assocTypesMap = {
	community(user, { can, cannot }, communities) {
		const communityIds = map(communities, "id");

		const grantAccess = user.roles.some(
			role => ["Advocate", "Support", "Advanced Support", "Admin"].includes(role)
		);
		if (grantAccess) {
			// If user has any of those roles, allow access to any community
			can("read", "Community");
		}
		else {
			// Else grant access based on association
			cannot("manage", "Community", { id: { $nin: communityIds } }).because(
				"User has no association to this community"
			);
			can("read", "Community", { id: { $in: communityIds } });
		}
	},
};
export const supportedAssocTypes = Object.freeze(Object.keys(assocTypesMap));

export function defineAbilityFor(user) {
	const builder = new AbilityBuilder(Ability);

	// Assign permissions based on k4 roles
	const roles = get(user, "roles", []);
	for (const role of roles)
		if (isFunction(rolesMap[role])) rolesMap[role](user, builder);

	// Assign permissions based on k4 permissions attached to roles
	const permissions = get(user, "permissions", []);

	for (const permission of permissions)
		if (isFunction(permissionsMap[permission]))
			permissionsMap[permission](user, builder);

	const associations = get(user, "associations", []);
	const associationsByType = groupBy(associations, "type");
	for (const assocT in associationsByType)
		if (isFunction(assocTypesMap[assocT]))
			assocTypesMap[assocT](user, builder, associationsByType[assocT]);

	return builder.build();
}

// Utility function to throw an error if ability check fails.
export function throwUnlessCan(ability, ...args) {
	return ForbiddenError.from(ability).throwUnlessCan(...args);
}
